import React from 'react';
import { ExpertiseTimelineProps } from '../../types/expertise-detail.types';
import {
  getTimelineStepsFromStatus,
  getCompletionPercentageFromStatus,
  getStageIcon,
} from '../../constants/expertise-status.constants';

export const ExpertiseTimeline: React.FC<ExpertiseTimelineProps> = ({
  // currentStep,
  completionPercentage,
  status: _status,
  createdDate,
  scheduledDate,
  statusAffaire,
}) => {
  const actualPercentage = statusAffaire 
    ? getCompletionPercentageFromStatus(statusAffaire) 
    : completionPercentage;
  const steps = statusAffaire 
    ? getTimelineStepsFromStatus(statusAffaire)
    : getTimelineStepsFromStatus('');
  
  // Get status configuration from constants
  const statusIcon = statusAffaire ? getStageIcon(statusAffaire) : '📌';

  return (
    <div className="timeline-section">
      <div className="timeline-header">
        <div className="timeline-title-group">
          <h2 className="timeline-title">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ marginRight: '8px' }}>
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
              <path d="M12 6v6l4 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
            </svg>
            Progression de l'expertise
          </h2>
          <div className="timeline-stats">
            {statusAffaire && (
              <div className="stat-badge affaire-status">
                <span className="stat-icon" style={{ marginRight: '6px' }}>{statusIcon}</span>
                <span className="stat-label">{statusAffaire}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="timeline-content">
        <div className="timeline-track">
          <div 
            className="timeline-progress-fill" 
            style={{ width: `${actualPercentage}%` }}
          >
            <div className="progress-glow"></div>
          </div>
          {steps.map((step, index) => {
            const position = (index / (steps.length - 1)) * 100;
            return (
              <div 
                key={step.id} 
                className="timeline-step"
                style={{ left: `${position}%` }}
              >
                {/* Date positioned above the timeline */}
                {step.title === 'Demande' && createdDate && (
                  <div className="timeline-date-top">
                    <span className="date-badge created-date">{createdDate}</span>
                  </div>
                )}
                {step.title === 'Visite' && scheduledDate && (
                  <div className="timeline-date-top">
                    <span className="date-badge visit-date">{scheduledDate}</span>
                  </div>
                )}
                
                <div className={`timeline-marker ${step.status}`}>
                  {step.status === 'completed' ? (
                    <svg className="check-icon" viewBox="0 0 16 16" fill="currentColor">
                      <path d="M13.5 3.5L6 11l-3.5-3.5L4 6l2 2 6-6.5L13.5 3.5z"/>
                    </svg>
                  ) : step.status === 'current' ? (
                    <div className="current-indicator">
                      <span className="current-dot"></span>
                    </div>
                  ) : (
                    <span className="step-number">{step.id}</span>
                  )}
                </div>
                <div className="timeline-label">
                  <span className="label-text">{step.title}</span>
                  {/* Only show status for steps without dates */}
                  {step.title !== 'Demande' && step.title !== 'Visite' && (
                    <>
                      {step.status === 'completed' && (
                        <span className="label-date">Terminé</span>
                      )}
                      {step.status === 'current' && (
                        <span className="label-date current">En cours</span>
                      )}
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <style>{`
        .timeline-section {
          margin-bottom: 28px;
          padding: 24px;
          background: #FFFFFF;
          border-radius: 20px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
          border: 1px solid #E5E7EB;
          position: relative;
          overflow: visible;
        }
        
        .timeline-section::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          border-radius: 20px 20px 0 0;
        }

        /* Enhanced header */
        .timeline-header {
          margin-bottom: 32px;
          position: relative;
        }

        .timeline-title-group {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          align-items: center;
          gap: 16px;
        }

        .timeline-title {
          font-size: 18px;
          font-weight: 600;
          color: #002035;
          margin: 0;
          display: flex;
          align-items: center;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .timeline-stats {
          position: absolute !important;
          top: 0 !important;
          right: 0 !important;
          display: flex !important;
          gap: 12px !important;
        }
        
        .timeline-section .stat-badge {
          display: inline-flex !important;
          align-items: center !important;
          gap: 6px !important;
          padding: 8px 16px !important;
          background: white !important;
          border: 1px solid #E5E7EB !important;
          border-radius: 20px !important;
          transition: all 0.3s ease !important;
          line-height: 1 !important;
          font-size: inherit !important;
          font-weight: inherit !important;
          height: 36px !important;
        }
        
        .timeline-section .stat-badge:hover {
          transform: translateY(-2px) !important;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1) !important;
        }
        
        .timeline-section .stat-badge.affaire-status {
          background: #FEF2F2 !important;
          border: 1px solid #ef5343 !important;
          color: #ef5343 !important;
          font-size: 13px !important;
          font-weight: 600 !important;
          transition: all 0.3s ease !important;
          white-space: nowrap !important;
        }
        
        .timeline-section .stat-badge.affaire-status:hover {
          background: #fee8e6 !important;
          border-color: #ef5343 !important;
          transform: translateY(-2px) !important;
          box-shadow: 0 6px 20px rgba(239, 83, 67, 0.15) !important;
        }
        
        .timeline-section .stat-badge.affaire-status .stat-label {
          color: #ef5343 !important;
          font-weight: 600 !important;
          font-size: 13px !important;
          line-height: 1 !important;
        }
        
        .timeline-section .stat-badge.affaire-status .stat-icon {
          color: #ef5343 !important;
          font-size: 15px !important;
          line-height: 1 !important;
          width: auto !important;
          height: auto !important;
          display: inline-flex !important;
          align-items: center !important;
          background: transparent !important;
          border-radius: 0 !important;
        }
        
        
        .timeline-section .stat-value {
          font-size: 18px !important;
          font-weight: 600 !important;
          color: #002035 !important;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          line-height: 1 !important;
        }
        
        .timeline-section .stat-icon {
          font-size: 18px !important;
          line-height: 1 !important;
          width: auto !important;
          height: auto !important;
          display: inline !important;
          background: transparent !important;
          border-radius: 0 !important;
        }
        
        .timeline-section .stat-label {
          font-size: 12px !important;
          color: #6B7280 !important;
          font-weight: 600 !important;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif !important;
          line-height: 1 !important;
        }

        /* Enhanced timeline with gradient progress */
        .timeline-content {
          position: relative;
          padding: 40px 0 70px 0;
        }

        .timeline-track {
          position: relative;
          height: 4px;
          background: #E5E7EB;
          border-radius: 50px;
          margin: 0 30px;
          box-shadow: inset 0 2px 4px rgba(0, 0, 0, 0.06);
        }

        .timeline-progress-fill {
          position: absolute;
          top: 0;
          left: 0;
          height: 100%;
          background: #002035;
          border-radius: 50px;
          transition: width 0.8s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 1;
          overflow: hidden;
          opacity: 0.9;
        }
        
        .progress-glow {
          display: none;
        }

        .timeline-step {
          position: absolute;
          top: -18px;
          transform: translateX(-50%);
          display: flex;
          flex-direction: column;
          align-items: center;
          z-index: 2;
        }
        
        /* Date positioned above timeline */
        .timeline-date-top {
          position: absolute;
          top: -50px;
          left: 50%;
          transform: translateX(-50%);
          white-space: nowrap;
          z-index: 5;
        }
        
        .date-badge {
          display: inline-block;
          padding: 4px 10px;
          font-size: 11px;
          font-weight: 600;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
          transition: all 0.2s ease;
          position: relative;
        }
        
        .date-badge.created-date {
          background: white;
          color: #0369A1;
          border: 1px solid #DBEAFE;
        }
        
        .date-badge.visit-date {
          background: white;
          color: #DC2626;
          border: 1px solid #FEE2E2;
        }
        
        .date-badge:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
        }

        .timeline-marker {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 13px;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          background: white;
          border: 3px solid #E5E7EB;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }

        .timeline-marker:hover {
          transform: scale(1.15) translateY(-4px);
          box-shadow: 0 8px 24px rgba(0, 0, 0, 0.15);
        }

        .timeline-marker.completed {
          background: #10B981;
          border-color: #10B981;
          color: white;
          box-shadow: 0 4px 16px rgba(16, 185, 129, 0.3);
        }

        .timeline-marker.current {
          background: #ef5343;
          border-color: #ef5343;
          color: white;
          box-shadow: 0 4px 16px rgba(239, 83, 67, 0.4);
        }
        
        .current-indicator {
          position: relative;
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .timeline-marker.pending {
          background: #F9FAFB;
          border-color: #CBD5E1;
          color: #9CA3AF;
        }

        .check-icon {
          width: 20px;
          height: 20px;
        }

        .current-dot {
          width: 12px;
          height: 12px;
          background: white;
          border-radius: 50%;
        }

        .step-number {
          font-size: 14px;
        }

        .timeline-label {
          margin-top: 12px;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 2px;
          padding-bottom: 10px;
          z-index: 10;
          position: relative;
          min-width: 120px;
        }
        
        .label-text {
          font-size: 12px;
          font-weight: 600;
          color: #002035;
          text-align: center;
          max-width: 100px;
          line-height: 1.4;
          font-family: 'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
        }
        
        .label-date {
          font-size: 10px;
          color: #6B7280;
          font-weight: 500;
          padding: 2px 8px;
          background: #F3F4F6;
          border-radius: 10px;
        }
        
        .label-date.current {
          background: #FEF2F2;
          color: #ef5343;
        }
        

        /* Tablet responsive */
        @media (max-width: 1024px) {
          .timeline-section {
            padding: 20px;
          }

          .timeline-track {
            margin: 0 20px;
          }

          .timeline-marker {
            width: 40px;
            height: 40px;
            border-width: 3px;
          }

          .label-text {
            font-size: 11px;
            max-width: 80px;
          }
          
          .timeline-date-top {
            top: -45px;
          }
          
          .date-badge {
            font-size: 11px;
            padding: 5px 10px;
          }
        }

        /* Mobile responsive */
        @media (max-width: 768px) {
          .timeline-section {
            padding: 16px;
            margin-bottom: 20px;
          }
          
          .timeline-title {
            font-size: 16px;
          }
          
          .timeline-title svg {
            width: 18px;
            height: 18px;
          }
          
          .timeline-stats {
            position: static !important;
            margin-top: 12px !important;
            flex-wrap: wrap;
          }
          
          .timeline-title-group {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          
          .timeline-section .stat-badge {
            padding: 6px 12px !important;
            height: 32px !important;
          }
          
          .timeline-section .stat-value {
            font-size: 16px !important;
            font-weight: 600 !important;
          }
          
          .timeline-section .stat-label {
            font-size: 11px !important;
          }

          .timeline-content {
            padding: 60px 0 50px 0;
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
          }

          .timeline-track {
            width: max(100%, 450px);
            margin: 0 25px;
            height: 6px;
          }
          
          .timeline-step {
            top: -16px;
          }

          .timeline-marker {
            width: 36px;
            height: 36px;
            border-width: 3px;
          }

          .check-icon {
            width: 16px;
            height: 16px;
          }

          .step-number {
            font-size: 14px;
          }
          
          .label-text {
            font-size: 10px;
            max-width: 70px;
          }
          
          .label-date {
            font-size: 9px;
          }
          
          .timeline-date-top {
            top: -40px;
          }
          
          .date-badge {
            font-size: 10px;
            padding: 3px 8px;
          }
        }

        /* Small mobile responsive */
        @media (max-width: 480px) {
          .timeline-section {
            padding: 10px;
            margin-bottom: 12px;
            border-radius: 8px;
          }

          .timeline-header {
            margin-bottom: 12px;
          }

          .timeline-title-group {
            gap: 6px;
          }

          .timeline-title {
            font-size: 13px;
          }

          .timeline-percentage {
            font-size: 16px;
          }

          .timeline-status {
            font-size: 10px;
            padding: 3px 8px;
          }

          .timeline-content {
            padding: 12px 0;
          }

          .timeline-track {
            height: 3px;
            margin: 0 30px;
          }

          .timeline-marker {
            width: 20px;
            height: 20px;
          }

          .timeline-step {
            top: -8px;
          }

          .timeline-label {
            top: 32px;
            width: 50px;
            font-size: 8px;
          }
        }

        /* Ultra small devices */
        @media (max-width: 360px) {
          .timeline-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 6px;
          }

          .timeline-track {
            margin: 0 20px;
          }

          .timeline-label {
            width: 40px;
            font-size: 7px;
          }
        }
      `}</style>
    </div>
  );
};